import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPage from "../../layout/index";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import axios from "../../api/config";
import { getHeader } from "../../utility/index";
import { toast, ToastContainer } from "react-toastify";

interface EmployeeDetails {
  id?: string;
  emp_id?: string;
  name_eng?: string;
  name_arb?: string;
  role?: string;
  sales_office?: string;
  supervisor_name?: string;
  [key: string]: any;
}

const TripReset: React.FC = () => {
  const { t } = useTranslation();
  const [empId, setEmpId] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState<EmployeeDetails | null>(null);
  const [resetResult, setResetResult] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [employeeNotFound, setEmployeeNotFound] = useState(false);
  const [errorMessage, setErrorMessage] = useState(""); // <-- Added for error display

  const validateEmpId = (id: string): boolean => {
    return id.length <= 10;
  };

  const handleEmpIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmpId(value);

    // Clear previous data when employee ID changes
    if (employeeDetails || resetResult || errorMessage) {
      setEmployeeDetails(null);
      setResetResult("");
      setErrorMessage("");
    }
  };

  const handleEmpIdBlur = async () => {
    if (!validateEmpId(empId) || !empId.trim()) {
      return;
    }

    setLoading(true);
    setEmployeeNotFound(false); // Reset not found state
    try {
      const response = await axios.get<any>(
        `/admin/employees/${empId}`,
        { headers: getHeader() }
      );
      if (!response.data || Object.keys(response.data).length === 0) {
        setEmployeeDetails(null);
        setEmployeeNotFound(true);
      } else {
        setEmployeeDetails(response.data);
        setEmployeeNotFound(false);
      }
    } catch (error: any) {
      console.error("Error fetching employee details:", error);
      setEmployeeDetails(null);
      setEmployeeNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isEmpIdValid && !loading) {
      handleEmpIdBlur();
    }
  };

  const handleResetEndDay = async () => {
    if (!employeeDetails) {
      return;
    }

    setResetLoading(true);
    setErrorMessage(""); // Clear previous error
    try {
      const response = await axios.post<any>(
        `/admin/trips/reset`,
        { emp_id: empId },
        { headers: getHeader() }
      );
      const message = response.data?.message || response.data?.data?.message || "End day reset successfully!";
      setResetResult(message);
      setErrorMessage(""); // Clear error if reset is successful
    } catch (error: any) {
      console.error("Error resetting end day:", error);
      const detail = error?.response?.data?.detail;
      let msg = "";
      if (typeof detail === "string") {
        msg = detail;
      } else if (detail?.message) {
        msg = detail.message;
      } else {
        msg = "Failed to reset end day. Please try again.";
      }
      setErrorMessage(msg); // Set error message here
      setResetResult(""); // Clear result if error occurs
    } finally {
      setResetLoading(false);
    }
  };

  const handleClear = () => {
    setEmpId("");
    setEmployeeDetails(null);
    setResetResult("");
    setEmployeeNotFound(false);
    setErrorMessage(""); // Clear error on clear
  };

  const isEmpIdValid = validateEmpId(empId);
  const canResetEndDay = employeeDetails && !resetLoading;

  return (
    <DashboardPage>
      <ToastContainer />
      <div className="bg-gray-50 sm:pl-0 pr-4">
        <div className="px-6 flex flex-col gap-2">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-0">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Reset Trip
            </h1>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Employee ID Input */}
            <div className="mb-6">
              <label htmlFor="empId" className="block text-sm font-medium text-gray-700 mb-2">
                Employee ID *
              </label>
              <div className="flex gap-3">
                <div className="relative w-80">
                  <input
                    id="empId"
                    type="text"
                    value={empId}
                    onChange={handleEmpIdChange}
                    onBlur={handleEmpIdBlur}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Employee ID"
                    autoComplete="off"
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan ${empId && !isEmpIdValid
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-cyan"
                      }`}
                    maxLength={10}
                  />
                  <button
                    onClick={handleEmpIdBlur}
                    disabled={!isEmpIdValid || loading}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-md transition-colors ${isEmpIdValid && !loading
                      ? "text-cyan hover:bg-cyan hover:text-white"
                      : "text-gray-400 cursor-not-allowed"
                      }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
                {loading && (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading...</span>
                  </div>
                )}
              </div>
              {empId && !isEmpIdValid && (
                <p className="text-red-500 text-sm mt-1">
                  Invalid Employee ID. Please enter a valid employee ID.
                </p>
              )}
              {isEmpIdValid && !loading && (
                <p className="text-gray-500 text-sm mt-1">
                  Click the search icon or press Enter to fetch employee details
                </p>
              )}
              {employeeNotFound && !loading && (
                <p className="text-red-500 text-sm mt-2">
                  Employee not found. Please check the Employee ID and try again.
                </p>
              )}
            </div>

            {/* Employee Details */}
            {loading && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Employee Details
                </h3>
                <div className="flex items-center justify-center py-8">
                  <Loader />
                </div>
              </div>
            )}

            {employeeDetails && !loading && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Employee Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Employee Name</p>
                    <p className="font-medium">{employeeDetails.name_eng || employeeDetails.name_arb || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Role</p>
                    <p className="font-medium">{employeeDetails.position_name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Sales Office</p>
                    <p className="font-medium">{employeeDetails.sales_office_name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Supervisor Name</p>
                    <p className="font-medium">{employeeDetails.supervisor_name || "N/A"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Reset Result or Error Display */}
            {(resetResult || errorMessage) && (
              <div className={`mb-6 p-6 ${errorMessage ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"} rounded-lg`}>
                <h3 className={`text-base font-semibold mb-3 ${errorMessage ? "text-red-900" : "text-green-900"}`}>
                  {errorMessage ? "Error" : "Success"}
                </h3>
                <div className="text-center">
                  {errorMessage ? (
                    <p className="text-xl font-bold text-red-600 tracking-wider">
                      {errorMessage}
                    </p>
                  ) : (
                    <p className="text-xl font-bold text-green-600 tracking-wider">
                      {resetResult}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Reset End Day Button */}
            {employeeDetails && (
              <div className="mb-6">
                <Button
                  label={resetLoading ? "Resetting..." : "Reset End Day"}
                  type="primary"
                  disabled={!canResetEndDay}
                  onClick={handleResetEndDay}
                />
              </div>
            )}

            {/* Clear Button */}
            {(employeeDetails || resetResult || errorMessage) && (
              <div className="flex gap-3">
                <Button
                  label="Clear All"
                  type="secondary"
                  onClick={handleClear}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardPage>
  );
};

export default TripReset;